import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../hooks/useLanguage';
import { SEO } from '../components/SEO';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { events, EventType } from '../data/eventsData';
import { Calendar, Clock, MapPin, User, Filter, GraduationCap, Video, Users, ArrowRight } from 'lucide-react';
export function EventsPage() {
  const {
    language,
    t
  } = useLanguage();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<EventType | 'all'>('all');
  const filteredEvents = filter === 'all' ? events : events.filter(e => e.type === filter);
  const featuredEvent = events.find(e => e.isFeatured);
  const getBadgeColor = (type: EventType) => {
    switch (type) {
      case 'webinar':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'workshop':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'training':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      default:
        return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };
  const getTypeIcon = (type: EventType) => {
    switch (type) {
      case 'webinar':
        return <Video className="w-4 h-4 mr-1" data-id="element-759" />;
      case 'workshop':
        return <Users className="w-4 h-4 mr-1" data-id="element-760" />;
      case 'training':
        return <GraduationCap className="w-4 h-4 mr-1" data-id="element-761" />;
    }
  };
  const handleRegister = (eventId: string) => {
    navigate(`/${language}/cyber-quiz?event=${eventId}`);
  };
  const getFilterLabel = (type: string) => {
    switch (type) {
      case 'all':
        return t('events.all');
      case 'webinar':
        return t('events.webinar');
      case 'workshop':
        return t('events.workshop');
      case 'training':
        return t('events.training');
      default:
        return type;
    }
  };
  return <div className="min-h-screen bg-slate-50 pb-20" data-id="element-762">
      <SEO title={t('seo.events.title')} description={t('seo.events.desc')} data-id="element-763" />

      {/* Hero Section */}
      <div className="bg-primary-900 text-white py-16 sm:py-24 relative overflow-hidden" data-id="element-764">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1550751827-4bd374c3f58b?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80')] opacity-10 bg-cover bg-center" data-id="element-765" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-766">
          <h1 className="text-4xl sm:text-5xl font-bold mb-6 tracking-tight" data-id="element-767">
            {t('events.title')}
          </h1>
          <p className="text-xl text-primary-100 max-w-2xl mx-auto mb-8" data-id="element-768">
            {t('events.subtitle')}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10 relative z-20" data-id="element-769">
        {/* Filters */}
        <div className="bg-white p-4 rounded-xl shadow-lg border border-slate-100 mb-12 flex flex-wrap items-center justify-center gap-2 sm:gap-4" data-id="element-770">
          <div className="flex items-center mr-2 text-slate-500 font-medium" data-id="element-771">
            <Filter className="w-5 h-5 mr-2" data-id="element-772" />
            {t('events.filter')}
          </div>
          {(['all', 'webinar', 'workshop', 'training'] as const).map(type => <button key={type} onClick={() => setFilter(type)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${filter === type ? 'bg-primary-900 text-white shadow-md' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-773">
              {getFilterLabel(type)}
            </button>)}
        </div>

        {/* Featured Event (if exists and matches filter) */}
        {featuredEvent && (filter === 'all' || filter === featuredEvent.type) && <div className="mb-12" data-id="element-774">
              <div className="flex items-center mb-4" data-id="element-775">
                <span className="h-px flex-1 bg-slate-200" data-id="element-776"></span>
                <span className="px-4 text-sm font-semibold text-slate-500 uppercase tracking-wider" data-id="element-777">
                  {t('events.featured')}
                </span>
                <span className="h-px flex-1 bg-slate-200" data-id="element-778"></span>
              </div>
              <Card variant="premium" className="overflow-hidden" onClick={() => handleRegister(featuredEvent.id)} data-id="element-779">
                <div className="flex flex-col md:flex-row gap-8" data-id="element-780">
                  <div className="md:w-1/3 bg-slate-100 rounded-lg min-h-[200px] flex items-center justify-center" data-id="element-781">
                    {/* Placeholder for event image */}
                    <Video className="w-20 h-20 text-slate-300" data-id="element-782" />
                  </div>
                  <div className="md:w-2/3 flex flex-col justify-center" data-id="element-783">
                    <div className="flex items-center gap-2 mb-3" data-id="element-784">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getBadgeColor(featuredEvent.type)}`} data-id="element-785">
                        {getTypeIcon(featuredEvent.type)}
                        {t(`events.${featuredEvent.type}` as any).toUpperCase()}
                      </span>
                      <span className="text-sm text-slate-500 flex items-center" data-id="element-786">
                        <Calendar className="w-4 h-4 mr-1" data-id="element-787" />
                        {featuredEvent.date}
                      </span>
                    </div>
                    <h2 className="text-2xl font-bold text-slate-900 mb-3" data-id="element-788">
                      {featuredEvent.title[language] || featuredEvent.title['en']}
                    </h2>
                    <p className="text-slate-600 mb-6 text-lg" data-id="element-789">
                      {featuredEvent.description[language] || featuredEvent.description['en']}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6" data-id="element-790">
                      <div className="flex items-center text-slate-600" data-id="element-791">
                        <Clock className="w-5 h-5 mr-3 text-primary-500" data-id="element-792" />
                        {featuredEvent.time}
                      </div>
                      <div className="flex items-center text-slate-600" data-id="element-793">
                        <MapPin className="w-5 h-5 mr-3 text-primary-500" data-id="element-794" />
                        {featuredEvent.location[language] || featuredEvent.location['en']}
                      </div>
                      <div className="flex items-center text-slate-600 sm:col-span-2" data-id="element-795">
                        <User className="w-5 h-5 mr-3 text-primary-500" data-id="element-796" />
                        {featuredEvent.speaker}
                      </div>
                    </div>
                    <div data-id="element-797">
                      <Button variant="accent" size="lg" onClick={e => {
                  e.stopPropagation();
                  handleRegister(featuredEvent.id);
                }} rightIcon={<ArrowRight className="w-5 h-5" data-id="element-799" />} data-id="element-798">
                        {t('events.registerNow')}
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            </div>}

        {/* Events Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-800">
          {filteredEvents.filter(e => !e.isFeatured || filter !== 'all') // Avoid duplicating featured event in 'all' view
        .map(event => <Card key={event.id} variant="elevated" className="flex flex-col h-full hover:shadow-xl transition-shadow duration-300" onClick={() => handleRegister(event.id)} data-id="element-801">
                <div className="mb-4" data-id="element-802">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getBadgeColor(event.type)}`} data-id="element-803">
                    {getTypeIcon(event.type)}
                    {t(`events.${event.type}` as any).toUpperCase()}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3 line-clamp-2" data-id="element-804">
                  {event.title[language] || event.title['en']}
                </h3>
                <p className="text-slate-600 mb-6 flex-grow line-clamp-3" data-id="element-805">
                  {event.description[language] || event.description['en']}
                </p>

                <div className="space-y-3 mb-6 border-t border-slate-100 pt-4" data-id="element-806">
                  <div className="flex items-center text-sm text-slate-600" data-id="element-807">
                    <Calendar className="w-4 h-4 mr-3 text-slate-400" data-id="element-808" />
                    {event.date}
                  </div>
                  <div className="flex items-center text-sm text-slate-600" data-id="element-809">
                    <Clock className="w-4 h-4 mr-3 text-slate-400" data-id="element-810" />
                    {event.time}
                  </div>
                  <div className="flex items-center text-sm text-slate-600" data-id="element-811">
                    <MapPin className="w-4 h-4 mr-3 text-slate-400" data-id="element-812" />
                    {event.location[language] || event.location['en']}
                  </div>
                </div>

                <Button variant="outline" fullWidth onClick={e => {
            e.stopPropagation();
            handleRegister(event.id);
          }} className="mt-auto group-hover:bg-primary-50 group-hover:border-primary-200 group-hover:text-primary-700" data-id="element-813">
                  {t('events.register')}
                </Button>
              </Card>)}
        </div>
      </div>
    </div>;
}